import React, { useCallback, useEffect, useState } from 'react';
import { withRouter } from 'react-router-dom';
import { Alert, Button, Progress, Table, Tag } from 'antd';
import { useMaiaT } from '$app/i18n/useMaiaT';
import { Header, Title } from '$app/shared/components/Header';
import { Container } from '$app/shared/components/Container';
import Loading from '$app/shared/components/Loading';
import { confirmImportJob, downloadCsv, getImportJob, getImportResult, listImportRows } from './actions';
import { FINISHED_STATUSES, POLL_INTERVAL_MS, progressPercent, usePolling } from './importHelpers';

const STATUS_COLORS = {
  VALIDATED: 'blue',
  IMPORTING: 'gold',
  COMPLETED: 'green',
  COMPLETED_WITH_ERRORS: 'orange',
  FAILED: 'red',
};

const rowErrors = (rows) =>
  rows
    .filter((row) => row.errors && row.errors.length && ['INVALID', 'FAILED'].includes(row.status))
    .flatMap((row) =>
      row.errors.map((error, index) => ({
        key: `${row.rowNumber}-${index}`,
        rowNumber: row.rowNumber,
        column: error.column || '',
        message: error.message,
      }))
    );

export function GarageImportJobPage({ match, history, pollIntervalMs = POLL_INTERVAL_MS }) {
  const t = useMaiaT();
  const { jobId } = match.params;
  const [job, setJob] = useState(null);
  const [rows, setRows] = useState([]);
  const [loadError, setLoadError] = useState(false);
  const [pollError, setPollError] = useState(false);
  const [rowsError, setRowsError] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [confirmError, setConfirmError] = useState(null);
  const [downloadError, setDownloadError] = useState(null);

  const loadRows = useCallback(() => listImportRows(jobId).then(setRows), [jobId]);

  useEffect(() => {
    let cancelled = false;
    Promise.all([getImportJob(jobId), listImportRows(jobId)])
      .then(([loadedJob, loadedRows]) => {
        if (cancelled) return;
        setJob(loadedJob);
        setRows(loadedRows);
      })
      .catch((error) => {
        console.error('GarageImportJobPage load error', { jobId, error });
        if (!cancelled) setLoadError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [jobId]);

  const importing = Boolean(job && job.status === 'IMPORTING');

  usePolling(
    async () => {
      try {
        const latest = await getImportJob(jobId);
        setJob(latest);
        setPollError(false);
        if (!FINISHED_STATUSES.includes(latest.status)) return;
        try {
          await loadRows();
          setRowsError(false);
        } catch (error) {
          console.error('GarageImportJobPage rows error', { jobId, error });
          setRowsError(true);
        }
      } catch (error) {
        console.error('GarageImportJobPage poll error', { jobId, error });
        setPollError(true);
      }
    },
    importing,
    pollIntervalMs
  );

  const confirm = async () => {
    setConfirming(true);
    setConfirmError(null);
    try {
      setJob(await confirmImportJob(jobId));
    } catch (error) {
      console.error('GarageImportJobPage confirm error', { jobId, error });
      const status = error.response && error.response.status;
      setConfirmError(
        status === 409
          ? t(
              'maia.garageImport.confirm.conflict',
              'Another import is already running for this segment. Try again when it has finished.'
            )
          : t('maia.garageImport.confirm.error', 'The import could not be started. Please try again.')
      );
    } finally {
      setConfirming(false);
    }
  };

  const downloadResult = () => {
    setDownloadError(null);
    return getImportResult(jobId)
      .then(downloadCsv)
      .catch((error) => {
        console.error('GarageImportJobPage result download error', { jobId, error });
        setDownloadError(
          t('maia.garageImport.downloadError', 'The file could not be downloaded. Please try again.')
        );
      });
  };

  if (loadError) {
    return (
      <Container>
        <Alert
          type="error"
          showIcon
          message={t('maia.garageImport.loadError', 'The import could not be loaded.')}
        />
      </Container>
    );
  }
  if (!job) return <Loading />;

  const { progress } = job;
  const errors = rowErrors(rows);
  const showsProgress = ['IMPORTING', 'COMPLETED', 'COMPLETED_WITH_ERRORS'].includes(job.status);
  const toImport = progress.total - progress.invalid;

  return (
    <Container>
      <Header>
        <Title>{t('maia.garageImport.jobTitle', 'Garage import')}</Title>
        <div>
          {job.fileName} · {job.segment} ·{' '}
          {job.mode === 'CREATE'
            ? t('maia.garageImport.mode.create', 'Create garages')
            : t('maia.garageImport.mode.update', 'Update garages')}{' '}
          <Tag color={STATUS_COLORS[job.status]} data-testid="import-status">
            {job.status}
          </Tag>
        </div>
        <Button type="link" style={{ padding: 0 }} onClick={() => history.push('/imports')}>
          {t('maia.garageImport.backToImports', 'All imports')}
        </Button>
      </Header>

      <div style={{ padding: 16 }}>
        {job.status === 'FAILED' && <Alert type="error" showIcon message={job.error} />}

        {job.status === 'VALIDATED' && (
          <div style={{ marginBottom: 16 }}>
            {job.canConfirm ? (
              <Alert
                type="success"
                showIcon
                message={t('maia.garageImport.validated', '{count} rows are valid and ready to import.', {
                  count: toImport,
                })}
              />
            ) : (
              <Alert
                type="warning"
                showIcon
                message={t(
                  'maia.garageImport.invalid',
                  '{count} rows have errors. Fix them in your file and upload it again; nothing has been imported.',
                  { count: progress.invalid }
                )}
              />
            )}
            <Button
              type="primary"
              style={{ marginTop: 12 }}
              disabled={!job.canConfirm}
              loading={confirming}
              onClick={confirm}
            >
              {t('maia.garageImport.confirm', 'Import {count} garages', { count: toImport })}
            </Button>
            {confirmError && <Alert type="error" showIcon style={{ marginTop: 12 }} message={confirmError} />}
          </div>
        )}

        {showsProgress && (
          <div style={{ marginBottom: 16 }} data-testid="import-progress">
            <Progress
              percent={progressPercent(progress)}
              status={job.status === 'COMPLETED_WITH_ERRORS' ? 'exception' : importing ? 'active' : 'success'}
            />
            <div>
              {t('maia.garageImport.progress', '{done}/{total} done', {
                done: progress.done,
                total: toImport,
              })}
              {' · '}
              {t('maia.garageImport.progressSplit', '{succeeded} succeeded, {failed} failed', {
                succeeded: progress.succeeded,
                failed: progress.failed,
              })}
            </div>
            {pollError && (
              <Alert
                type="warning"
                showIcon
                style={{ marginTop: 8 }}
                message={t(
                  'maia.garageImport.pollError',
                  'Lost connection; retrying. The import keeps running.'
                )}
              />
            )}
          </div>
        )}

        {rowsError && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message={t(
              'maia.garageImport.rowsError',
              'The import finished, but its rows could not be loaded. Refresh the page.'
            )}
          />
        )}

        {job.status !== 'IMPORTING' && job.status !== 'FAILED' && (
          <div style={{ marginBottom: 16 }}>
            <Button onClick={downloadResult}>
              {t('maia.garageImport.downloadResult', 'Download results as CSV')}
            </Button>
            {downloadError && (
              <Alert type="error" showIcon style={{ marginTop: 12 }} message={downloadError} />
            )}
          </div>
        )}

        {errors.length > 0 && (
          <Table
            size="small"
            rowKey="key"
            dataSource={errors}
            pagination={{ pageSize: 20 }}
            columns={[
              { title: t('maia.garageImport.column.row', 'Row'), dataIndex: 'rowNumber', width: 80 },
              { title: t('maia.garageImport.column.column', 'Column'), dataIndex: 'column', width: 180 },
              { title: t('maia.garageImport.column.error', 'Error'), dataIndex: 'message' },
            ]}
          />
        )}
      </div>
    </Container>
  );
}

export default withRouter(GarageImportJobPage);
